import React, { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import qs from 'qs';
import { faLock } from '@fortawesome/pro-solid-svg-icons';

import { Container, Row, Col } from '../../../grid';
import Icon from '../../atoms/icon';
import CSLogo from '../../atoms/logo';
import Card from '../../organisms/card';
import { ChatUI } from '../../organisms/chats';

import {
  IChatThread,
  VerifyToken,
  VerifyTokenResponse
} from '../../../libs/chat';
import { ErrorResponse } from '../../../libs/request';
import { IBrand } from '../../../libs/brand';
import { IPatient } from '../../../libs/patient';

interface LogoProps {
  src?: string;
  altText?: string;
}

const Logo = ({ src, altText }: LogoProps): JSX.Element => {
  if (!src) {
    return <CSLogo maxWidth={100} />;
  }
  return (
    <img
      src={src}
      alt={altText ? altText : ''}
      className="sharp-image"
      style={{
        maxHeight: '44px'
      }}
    />
  );
};

interface ChatContainerProps {
  backgroundColor: string;
}

const LoadingContainer: React.FC<ChatContainerProps> = ({
  backgroundColor,
  children
}) => {
  return (
    <Container fluid fullscreen bg={backgroundColor}>
      <Row>
        <Col
          xs={16}
          offset={{
            xs: 4
          }}
          style={{
            paddingLeft: 0,
            paddingRight: 0
          }}
        >
          {children}
        </Col>
      </Row>
    </Container>
  );
};

interface BrandProps {
  brand: IBrand;
}

const Brand = ({ brand }: BrandProps): JSX.Element => {
  if (!brand) {
    return null;
  }
  if (brand.logo && brand.logo.length) {
    return <Logo src={brand.logo} altText={brand.name} />;
  }
  return <span>{brand.name}</span>;
};

const PatientChatPage = (): JSX.Element => {
  const [patient, setPatient] = useState<IPatient>(null);
  const [thread, setThread] = useState<IChatThread>(null);
  const [error, setError] = useState(null);
  const { search } = useLocation();
  const query = qs.parse(search, {
    ignoreQueryPrefix: true
  });
  const token = query && query.t ? (query.t as string) : null;
  const brand = thread && thread.brand ? thread.brand : null;
  const loadingCardStyle = { marginTop: '100px' };

  useEffect(() => {
    if (!token) {
      return;
    }
    VerifyToken({ token }).then((response) => {
      if (response.status === 'FAIL') {
        const errorMessage = (response as ErrorResponse).message;
        const message = errorMessage ? errorMessage : 'An error has occurred!';
        setError(message);
        return;
      }
      const patient = (response as VerifyTokenResponse).patient;
      const threads = (response as VerifyTokenResponse).threads;
      /**
       * For now there should only be one
       * text thread per patient
       */
      const thread = threads[0];
      setPatient(patient as IPatient);
      setThread(thread);
    });
  }, [token]);

  if (!token) {
    return <Navigate to="/" replace />;
  }

  if (!patient && !thread && !error) {
    return (
      <LoadingContainer backgroundColor="grey">
        <Card style={loadingCardStyle}>Verifying...</Card>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <LoadingContainer backgroundColor="grey">
        <Card style={loadingCardStyle}>{error}</Card>
      </LoadingContainer>
    );
  }

  if (!thread) {
    return (
      <LoadingContainer backgroundColor="grey">
        <Card style={loadingCardStyle}>
          There doesn't seem to be a chat associated with you at this time!
        </Card>
      </LoadingContainer>
    );
  }

  return (
    <Container fluid bg="grey">
      <Row>
        <Col
          xs={24}
          style={{
            paddingLeft: 0,
            paddingRight: 0
          }}
        >
          <div className="bg-white h-screen">
            <div className="bg-white px-3 py-4 fixed w-full z-1">
              <Brand brand={brand} />
              <div className="inline-block text-sans-sm pt-4 pl-3 text-black align-top ml-4 text-center">
                <Icon icon={faLock} className="mr-2 text-gray3" />
                Online chat is secure.
              </div>
            </div>
            <ChatUI thread={thread} isPatientFacing />
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default PatientChatPage;
